import store from '../store/index';
import { RESET_AUTH } from '../store/auth/duck';
import { AUTO_TRIED_BIOMETRIC_LOGIN_SESSION_KEY } from '../constants/session';
import NativeOrWeb from './native';
import { classifyAuthRoute } from './auth-endpoints';
import {
  attemptTokenRefresh,
  currentAuthEpoch,
  isLogoutPending,
  isSessionDeadLatched,
  LOGOUT_PENDING_KEY,
  LOGOUT_RETRY_CANCEL_KEY,
  bumpAuthEpoch,
  stopLogoutRetry,
  resetAuthOnce,
  latchAndPurgeDeadSession,
  enqueueCredentialWrite,
  __resetAuthRefreshStateForTests
} from './auth-refresh';
import { AuthRequestError, AuthSessionCancelledError } from './auth-session-errors';
import { readTokenExpiry } from './jwt';

const NEAR_EXP_MS = 60 * 1000;
let lostRotationInFlight = false;

const dispatchReset = () => {
  sessionStorage.removeItem(AUTO_TRIED_BIOMETRIC_LOGIN_SESSION_KEY);
  store.dispatch({ type: RESET_AUTH });
};

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', event => {
    if (event.key === LOGOUT_PENDING_KEY) {
      try {
        sessionStorage.removeItem(LOGOUT_PENDING_KEY);
      } catch (_) {
        // ignore
      }
      if (event.newValue === '1') {
        bumpAuthEpoch();
      }
    }
    if (event.key === LOGOUT_RETRY_CANCEL_KEY && event.newValue) {
      stopLogoutRetry();
    }
  });
}

const syncNativeTokensFromHeaders = async (response, epoch) => {
  if (!response || !response.headers || !NativeOrWeb.isNativePlatform()) {
    return;
  }
  const newRefreshToken = response.headers.get('x-refresh-token');
  const newAccessToken = response.headers.get('x-access-token');
  if (!newRefreshToken && !newAccessToken) {
    return;
  }
  await enqueueCredentialWrite(epoch, {
    accessToken: newAccessToken || undefined,
    refreshToken: newRefreshToken || undefined
  });
};

const optionsForPostRefreshRetry = async (url, options) => {
  const next = Object.assign({}, options || {});
  next.headers = Object.assign({}, (options && options.headers) || {});
  if (next.headers['x-refresh-token']) {
    delete next.headers['x-refresh-token'];
  }
  const route = classifyAuthRoute(url);
  if (route.pathClass === 'session_check_auth' && typeof next.body === 'string') {
    try {
      const parsed = JSON.parse(next.body);
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        Object.prototype.hasOwnProperty.call(parsed, 'accessToken')
      ) {
        const fresh = await NativeOrWeb.getAccessTokenCookie();
        if (fresh) {
          parsed.accessToken = fresh;
        } else {
          delete parsed.accessToken;
        }
        next.body = JSON.stringify(parsed);
      }
    } catch (_) {
      // leave body unchanged if it is not JSON
    }
  }
  return maybeAttachRefreshHeader(url, next);
};

const maybeAttachRefreshHeader = async (url, options) => {
  const route = classifyAuthRoute(url);
  if (!route.canAttachRefreshHeader || !NativeOrWeb.isNativePlatform()) {
    return options;
  }
  if (isLogoutPending() || isSessionDeadLatched()) {
    return options;
  }
  const accessToken = await NativeOrWeb.getAccessTokenCookie();
  const expiry = readTokenExpiry(accessToken);
  const nearExp = expiry.state === 'ok' && expiry.expiresAtMs - Date.now() <= NEAR_EXP_MS;
  if (!(nearExp || expiry.state === 'unreadable')) {
    return options;
  }
  const refreshToken = await NativeOrWeb.getRefreshTokenCookie();
  if (!refreshToken) {
    return options;
  }
  const headers = Object.assign({}, options.headers || {}, { 'x-refresh-token': refreshToken });
  return Object.assign({}, options, { headers });
};

const maybeRecoverLostRotation = async (url, includeCredentials, epoch) => {
  if (!includeCredentials || !NativeOrWeb.isNativePlatform()) {
    return;
  }
  if (!store.getState().auth.isAuthenticated) {
    return;
  }
  const route = classifyAuthRoute(url);
  if (!route.canRecoverLostRotation) {
    return;
  }
  if (isLogoutPending() || isSessionDeadLatched() || lostRotationInFlight) {
    return;
  }
  const accessToken = await NativeOrWeb.getAccessTokenCookie();
  const expiry = readTokenExpiry(accessToken);
  if (!(expiry.state === 'ok' && expiry.expiresAtMs - Date.now() <= NEAR_EXP_MS)) {
    return;
  }
  if (currentAuthEpoch() !== epoch) {
    return;
  }
  lostRotationInFlight = true;
  try {
    const outcome = await attemptTokenRefresh();
    if (currentAuthEpoch() !== epoch || outcome.result === 'cancelled') {
      return;
    }
  } finally {
    lostRotationInFlight = false;
  }
};

async function statusError(response, route, kind) {
  const errorMessage = await response.text();
  let code;
  let payload;
  try {
    payload = JSON.parse(errorMessage);
    code = payload && payload.code;
  } catch (_) {
    // body is not JSON
  }
  const error = new AuthRequestError({
    status: response.status,
    pathClass: route && route.pathClass,
    kind,
    message: 'request_failed',
    code
  });
  error.response = response;
  error.payload = payload;
  return error;
}

const checkStatus = async (response, url, options, epoch) => {
  const route = classifyAuthRoute(url);
  if (response.status === 500 && route.pathClass === 'session_check_auth') {
    throw new AuthRequestError({ status: 500, pathClass: route.pathClass, kind: 'transient' });
  }
  if ((response.status >= 200 && response.status < 300) || response.status === 422 || response.status === 500) {
    return response;
  }

  if (response.status === 401 || response.status === 403) {
    if (route.pathClass === 'refresh') {
      resetAuthOnce(dispatchReset, store.getState().auth.isAuthenticated);
      throw new AuthRequestError({ status: response.status, pathClass: route.pathClass, kind: 'session_dead' });
    }

    if (!route.canRefresh) {
      throw await statusError(response, route);
    }

    const outcome = await attemptTokenRefresh();
    if (outcome.result === 'cancelled' || currentAuthEpoch() !== epoch) {
      throw new AuthSessionCancelledError();
    }

    if (outcome.result === 'ok') {
      const retryOptions = await optionsForPostRefreshRetry(url, options);
      const retryResponse = await fetch(url, retryOptions);
      if (currentAuthEpoch() !== epoch) {
        throw new AuthSessionCancelledError();
      }
      await syncNativeTokensFromHeaders(retryResponse, epoch);
      // Codex #3: first /auth 500 is transient; the post-refresh /auth retry
      // must stay transient on 5xx (do not let api.authenticateUser treat an
      // empty 5xx body as session_dead). At-most-one retry — do not refresh again.
      if (route.pathClass === 'session_check_auth' && retryResponse.status >= 500) {
        throw new AuthRequestError({
          status: retryResponse.status,
          pathClass: route.pathClass,
          kind: 'transient'
        });
      }
      if (retryResponse.status !== 401 && retryResponse.status !== 403) {
        return retryResponse;
      }
      const kind =
        route.pathClass === 'session_check_auth' && retryResponse.status === 401 ? 'session_dead' : undefined;
      if (kind === 'session_dead') {
        await latchAndPurgeDeadSession();
      }
      throw await statusError(retryResponse, route, kind);
    }

    if (outcome.result === 'session_dead') {
      resetAuthOnce(dispatchReset, store.getState().auth.isAuthenticated);
      throw new AuthRequestError({
        status: 401,
        pathClass: route.pathClass,
        correlationId: outcome.correlationId,
        kind: 'session_dead'
      });
    }

    throw new AuthRequestError({
      status: outcome.httpStatus,
      pathClass: route.pathClass,
      correlationId: outcome.correlationId,
      kind: 'transient'
    });
  }

  throw await statusError(response, route);
};

export default async (url, options, includeCredentials = true) => {
  const epoch = currentAuthEpoch();
  options = options || {};
  if (includeCredentials) {
    options.credentials = 'include';
    // WebKit can replay a cached GET, including a rotated x-refresh-token header.
    // no-store keeps native credentialed calls off that cache. A caller-supplied
    // cache mode is left alone, and web requests are unchanged.
    if (NativeOrWeb.isNativePlatform() && !options.cache) {
      options.cache = 'no-store';
    }
  }
  const route = classifyAuthRoute(url);
  const cancelledByLogoutOrEpoch = () =>
    currentAuthEpoch() !== epoch || (isLogoutPending() && !route.tombstoneExempt);
  // Codex #4: cancel before send. Response-only checks miss in-flight work after
  // logout tap; tombstoneExempt still lets logout / refresh / public through.
  if (cancelledByLogoutOrEpoch()) {
    throw new AuthSessionCancelledError();
  }
  options = await maybeAttachRefreshHeader(url, options);
  if (cancelledByLogoutOrEpoch()) {
    throw new AuthSessionCancelledError();
  }

  return fetch(url, options).then(
    async response => {
      if (currentAuthEpoch() !== epoch) {
        throw new AuthSessionCancelledError();
      }
      if (isLogoutPending() && !classifyAuthRoute(url).tombstoneExempt) {
        throw new AuthSessionCancelledError();
      }
      await syncNativeTokensFromHeaders(response, epoch);
      return checkStatus(response, url, options, epoch);
    },
    async error => {
      await maybeRecoverLostRotation(url, includeCredentials, epoch);
      throw error;
    }
  );
};

export { __resetAuthRefreshStateForTests };
